<script setup lang="ts">
defineProps<{
  showOptionErrors?: boolean
  sizeChartModalOpen?: boolean
  hasSizeChart?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:showOptionErrors', value: boolean): void
  (e: 'update:sizeChartModalOpen', value: boolean): void
}>()

const { t } = useI18n()
const productStore = useProductStore()

// Track which option groups need to wiggle when incomplete selection is attempted
const optionGroupsToWiggle = ref<Record<string, boolean>>({})

function handleOptionGroupChange(optionGroupCode: string, optionCode: string) {
  productStore.handleOptionChange(optionGroupCode, optionCode)
  if (productStore.allOptionsSelected)
    emit('update:showOptionErrors', false)
}

// Reset the wiggle animation after it completes
function resetWiggleAnimation(optionGroupCode: string) {
  optionGroupsToWiggle.value[optionGroupCode] = false
}

// Function to mark unselected option groups to wiggle
function wiggleUnselectedOptionGroups() {
  const unselectedGroups = Object.keys(productStore.availableProductOptions).filter(
    groupCode => !productStore.selectedOptions[groupCode],
  )

  unselectedGroups.forEach((groupCode) => {
    optionGroupsToWiggle.value[groupCode] = true
    // Reset the animation after it completes
    setTimeout(() => resetWiggleAnimation(groupCode), 1020) // Animation duration + small buffer
  })
}

// Expose the wiggle function so it can be called from the parent when add to cart is attempted
defineExpose({ wiggleUnselectedOptionGroups })
</script>

<template>
  <div v-for="(options, optionGroupCode) in productStore.availableProductOptions" :key="optionGroupCode" class="mt4">
    <div class="flex justify-between">
      <span
        v-if="productStore.productOptionGroups?.[optionGroupCode]?.name"
        class="fw600 capitalize"
        :class="{
          'text-destructive': showOptionErrors && !productStore.selectedOptions[optionGroupCode],
        }"
      >
        {{ productStore.productOptionGroups[optionGroupCode].name }}:
        <span v-if="showOptionErrors && !productStore.selectedOptions[optionGroupCode]" class="text-sm">
          {{ t('product.error.select_option', [`"${productStore.productOptionGroups?.[optionGroupCode]?.name}"`]) }}
        </span>
      </span>
      <span v-if="optionGroupCode === 'size' && hasSizeChart">
        <UiButton
          variant="link"
          @click="emit('update:sizeChartModalOpen', true)"
        >
          <Icon name="i-ph:ruler-light" />
          {{ t('product.size_chart.title') }}
        </UiButton>
      </span>
    </div>

    <div
      class="flex flex-wrap gap2"
      :data-testid="`productOptionWrapper-${optionGroupCode}`"
      :class="{
        'animate-[wiggle_1s_ease-in-out] keyframes-wiggle': optionGroupsToWiggle[optionGroupCode],
      }"
      @animationend="resetWiggleAnimation(optionGroupCode)"
    >
      <button
        v-for="(option, optionCode, index) in options" :key="optionCode"
        :data-testid="`productOptionsOption-${index}`"
        class="group relative flex cursor-pointer items-center justify-center border rounded-md bg-white p0.75 text-sm text-context text-slate-900 fw600 uppercase shadow-sm hover:bg-slate-50 focus:outline-none"
        :class="{
          'opacity-25': !productStore.availableOptionsMap[optionGroupCode]?.[optionCode],
          'cursor-pointer hover:scale-105': productStore.availableOptionsMap[optionGroupCode]?.[optionCode],
          'ring-2 ring-orange-500 !bg-orange-500/5': productStore.selectedOptions[optionGroupCode] === optionCode,
          'border-gray-300': productStore.selectedOptions[optionGroupCode] !== optionCode,
          'opacity-40': productStore.previewOptionsMap[optionGroupCode] && !productStore.previewOptionsMap[optionGroupCode][optionCode],
          'py3 px4': !productStore.isColorGroupWithImages(optionGroupCode),
        }"
        @mouseenter="productStore.handleProductOptionMouseOver(optionGroupCode, optionCode)"
        @mouseleave="productStore.handleProductOptionMouseLeave"
        @click="handleOptionGroupChange(optionGroupCode, optionCode)"
      >
        <div>
          <template v-if="productStore.isColorGroupWithImages(optionGroupCode)">
            <NuxtImg
              :src="option.featuredAsset!.preview" :alt="option.name" class="size-15"
              :class="[
                productStore.availableOptionsMap[optionGroupCode]?.[optionCode] ? '' : 'op25 grayscale',
                productStore.previewOptionsMap[optionGroupCode]?.[optionCode] ? '' : '',
              ]"
              width="100"
              height="100"
              loading="lazy"
              data-testid="productImageView"
            />
          </template>
          <template v-else>
            {{ option.name }}
          </template>
        </div>

        <span
          v-if="productStore.availableOptionsMap[optionGroupCode]?.[optionCode]"
          class="pointer-events-none absolute border-2 border-transparent rounded-md -inset-px n-active:border n-checked:border-orange-500"
          aria-hidden="true"
        />
        <span
          v-else
          aria-hidden="true"
          class="pointer-events-none absolute border-2 border-slate-200 rounded-md n-dashed -inset-px"
        >
          <svg
            class="absolute inset-0 h-full w-full stroke-2 text-slate-200" viewBox="0 0 100 100"
            preserveAspectRatio="none" stroke="currentColor"
          >
            <line x1="0" y1="100" x2="100" y2="0" vector-effect="non-scaling-stroke" />
          </svg>
        </span>
      </button>
    </div>
  </div>
</template>
